import {
  Calendar,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CheckSquare,
  Clock,
  Mail,
  MapPin,
  Phone,
  Video,
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import { ActivityTypeIcon } from '../../settings/CustomActivityTypes';
import type { CustomActivityType } from '../../../lib/stores/activityTypesStore';

/**
 * Chip di tipo e di scadenza della modalità "Attività" del composer inline.
 * Stessa tavolozza e stesse scorciatoie dell'input rapido della Dashboard
 * (`dashboard/InlineActivityInput.tsx`): qui cambia solo la veste, resa Notion come
 * il resto del redesign della cronologia (mockup, vista 6b: `.cmp-chip`).
 */

export interface ComposerActivityTypeOption {
  value: string;
  label: string;
  icon: any;
  color: string;
}

/** Tipi di SISTEMA — identici, valori e colori, a quelli della Dashboard. */
export const COMPOSER_ACTIVITY_TYPES: ComposerActivityTypeOption[] = [
  { value: 'task', label: 'Task', icon: CheckSquare, color: '#F59E0B' },
  { value: 'call', label: 'Chiamata', icon: Phone, color: '#10B981' },
  { value: 'videochiamata', label: 'Video', icon: Video, color: '#8B5CF6' },
  { value: 'appuntamento', label: 'Appuntamento', icon: CalendarCheck, color: '#2383E2' },
  { value: 'sopralluogo', label: 'Sopralluogo', icon: MapPin, color: '#F97316' },
  { value: 'email', label: 'Email', icon: Mail, color: '#2383E2' },
  { value: 'other', label: 'Altro', icon: CalendarClock, color: '#787774' },
];

/** Colore unico dei tipi custom per-org, come in Dashboard. */
const CUSTOM_TYPE_COLOR = '#6366F1';

export const COMPOSER_DATE_OPTIONS = [
  { label: 'Oggi', value: 0 },
  { label: 'Domani', value: 1 },
  { label: 'Tra 3gg', value: 3 },
];

const CHIP_BASE =
  'inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] whitespace-nowrap transition-colors border border-transparent';
const CHIP_OFF =
  'bg-[rgba(55,53,47,0.05)] text-[#787774] hover:bg-[rgba(55,53,47,0.09)] dark:bg-white/[0.06] dark:text-gray-400 dark:hover:bg-white/[0.10]';
const CHIP_ON =
  'bg-[rgba(35,131,226,0.10)] text-[#2383E2] border-[rgba(35,131,226,0.3)] font-semibold dark:bg-[#2383E2]/20 dark:text-[#5CA5E8] dark:border-[#2383E2]/40';

export interface ComposerTypeChipsProps {
  selectedType: string;
  onSelect(type: string): void;
  customTypes: CustomActivityType[];
  disabled?: boolean;
}

export function ComposerTypeChips({ selectedType, onSelect, customTypes, disabled }: ComposerTypeChipsProps) {
  return (
    <>
      {COMPOSER_ACTIVITY_TYPES.map((option) => {
        const Icon = option.icon;
        const isSelected = selectedType === option.value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(option.value)}
            className={`${CHIP_BASE} ${isSelected ? '' : CHIP_OFF}`}
            style={
              isSelected
                ? {
                    backgroundColor: `${option.color}1F`,
                    borderColor: `${option.color}66`,
                    color: option.color,
                    fontWeight: 600,
                  }
                : undefined
            }
          >
            <Icon className="h-3 w-3" strokeWidth={1.5} />
            {option.label}
          </button>
        );
      })}
      {customTypes.map((customType) => {
        const isSelected = selectedType === customType.slug;
        return (
          <button
            key={customType.slug}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(customType.slug)}
            className={`${CHIP_BASE} ${isSelected ? '' : CHIP_OFF}`}
            style={
              isSelected
                ? {
                    backgroundColor: `${CUSTOM_TYPE_COLOR}1F`,
                    borderColor: `${CUSTOM_TYPE_COLOR}66`,
                    color: CUSTOM_TYPE_COLOR,
                    fontWeight: 600,
                  }
                : undefined
            }
          >
            <ActivityTypeIcon icon={customType.icon} className="h-3 w-3" strokeWidth={1.5} />
            {customType.name}
          </button>
        );
      })}
    </>
  );
}

export interface ComposerDateChipsProps {
  /** Id univoco per gli input nativi data/ora (più composer possono coesistere in pagina). */
  fieldIdPrefix: string;
  offsetDays: number | null;
  customDate: string;
  time: string;
  onOffsetChange(days: number): void;
  onCustomDateChange(date: string): void;
  onTimeChange(time: string): void;
  disabled?: boolean;
  /** `YYYY-MM-DD` di oggi: minimo del date picker, come in Dashboard. */
  minDate: string;
}

export function ComposerDateChips({
  fieldIdPrefix,
  offsetDays,
  customDate,
  time,
  onOffsetChange,
  onCustomDateChange,
  onTimeChange,
  disabled,
  minDate,
}: ComposerDateChipsProps) {
  const timeInputId = `${fieldIdPrefix}-time`;
  const openNativePicker = (id: string) => {
    const input = document.getElementById(id) as HTMLInputElement | null;
    if (input?.showPicker) input.showPicker();
    else input?.focus();
  };

  return (
    <>
      {COMPOSER_DATE_OPTIONS.map((option) => {
        const isSelected = offsetDays === option.value && !customDate;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            onClick={() => onOffsetChange(option.value)}
            className={`${CHIP_BASE} ${isSelected ? CHIP_ON : CHIP_OFF}`}
          >
            <Calendar className="h-3 w-3" strokeWidth={1.5} />
            {option.label}
          </button>
        );
      })}

      {/* "Altra": calendario react-datepicker (come nel drawer), NON il picker di sistema.
          La chip stessa fa da customInput: DatePicker le inietta l'onClick di apertura. */}
      <DatePicker
        selected={customDate ? new Date(`${customDate}T00:00:00`) : null}
        onChange={(date) => {
          if (!date) return;
          const y = date.getFullYear();
          const m = String(date.getMonth() + 1).padStart(2, '0');
          const d = String(date.getDate()).padStart(2, '0');
          onCustomDateChange(`${y}-${m}-${d}`);
        }}
        locale="it"
        dateFormat="dd/MM/yyyy"
        minDate={new Date(`${minDate}T00:00:00`)}
        portalId="root-portal"
        popperClassName="z-[80]"
        disabled={disabled}
        customInput={
          <button
            type="button"
            aria-label="Scegli un'altra data"
            className={`${CHIP_BASE} ${customDate ? CHIP_ON : CHIP_OFF}`}
          >
            <CalendarDays className="h-3 w-3" strokeWidth={1.5} />
            {customDate
              ? new Date(`${customDate}T00:00:00`).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })
              : 'Altra'}
          </button>
        }
      />

      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => openNativePicker(timeInputId)}
          className={`${CHIP_BASE} ${CHIP_OFF}`}
        >
          <Clock className="h-3 w-3" strokeWidth={1.5} />
          {time}
        </button>
        <input
          id={timeInputId}
          type="time"
          value={time}
          onChange={(e) => onTimeChange(e.target.value)}
          className="absolute inset-0 opacity-0 w-0 h-0"
          tabIndex={-1}
          aria-hidden
        />
      </div>
    </>
  );
}
